import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, ItemRequestApiRow, ItemRequestListResponse, ItemRequestVm } from '@/api/types'

function log(label: string, data: Record<string, unknown>) {
  try {
    console.groupCollapsed(label)
    for (const [k, v] of Object.entries(data)) console.log(k, v)
    console.groupEnd()
  } catch {
    // ignore
  }
}

function unwrap(payload: any) {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data
    if ('data' in payload) return payload.data
  }
  return payload
}

function toArray(payload: any): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') {
    if (Array.isArray((raw as any).data)) return (raw as any).data
    if (Array.isArray((raw as any).items)) return (raw as any).items
    if (Array.isArray((raw as any).result)) return (raw as any).result
  }
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function num(v: any, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function str(v: any) {
  return typeof v === 'string' ? v : String(v ?? '')
}

function mapRow(raw: any): ItemRequestApiRow {
  return {
    id: num(raw?.id ?? raw?.Id),
    empId: raw?.empId ?? raw?.EmpId,
    itemId: raw?.itemId ?? raw?.ItemId,
    departmentId: raw?.departmentId ?? raw?.DepartmentId,
    quantity: raw?.quantity ?? raw?.Quantity,
    requestedDate: raw?.requestedDate ?? raw?.RequestedDate,
    details: raw?.details ?? raw?.Details,
    status: raw?.status ?? raw?.Status,
    approvalDate: raw?.approvalDate ?? raw?.ApprovalDate,
    approvalBy: raw?.approvalBy ?? raw?.ApprovalBy,
    branchId: raw?.branchId ?? raw?.BranchId,
    isEditable: raw?.isEditable ?? raw?.IsEditable,
    itemName: raw?.itemName ?? raw?.ItemName,
    itemCode: raw?.itemCode ?? raw?.ItemCode,
    itemNameEn: raw?.itemNameEn ?? raw?.ItemNameEn,
    itemNameAr: raw?.itemNameAr ?? raw?.ItemNameAr,
  }
}

function normalizeVm(raw: any, branchId: number): ItemRequestVm {
  return {
    id: num(raw?.id ?? raw?.Id),
    empId: num(raw?.empId ?? raw?.EmpId),
    itemId: num(raw?.itemId ?? raw?.ItemId),
    departmentId: num(raw?.departmentId ?? raw?.DepartmentId),
    quantity: num(raw?.quantity ?? raw?.Quantity),
    requestedDate: str(raw?.requestedDate ?? raw?.RequestedDate),
    details: str(raw?.details ?? raw?.Details),
    status: num(raw?.status ?? raw?.Status),
    approvalDate: str(raw?.approvalDate ?? raw?.ApprovalDate) || undefined,
    approvalBy: str(raw?.approvalBy ?? raw?.ApprovalBy) || undefined,
    branchId: num(raw?.branchId ?? raw?.BranchId, branchId),
    isEditable: raw?.isEditable ?? raw?.IsEditable,
  }
}

export class ItemRequestApiService {
  async getAll(branchId: number, pageNumber: number, pageSize: number, status?: number): Promise<ItemRequestListResponse> {
    const url = endpoints.itemRequest.getAll(branchId, pageNumber, pageSize, status)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    log('[ItemRequestApi.getAll] Response', { url, status: response.status, response: response.data })
    const payload = response.data
    const raw = unwrap(payload)
    const list = toArray(raw).map(mapRow)
    const totalRecords = num(raw?.totalRecords ?? payload?.totalRecords ?? raw?.totalCount ?? payload?.totalCount, list.length)
    return { totalRecords, pageNumber, pageSize, data: list }
  }

  async getById(id: number, branchId: number): Promise<ItemRequestVm | null> {
    const url = endpoints.itemRequest.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    log('[ItemRequestApi.getById] Response', { url, status: response.status, response: response.data })
    const payload = response.data
    const raw = unwrap(payload)
    if (!raw) return null
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    if (!source || typeof source !== 'object') return null
    return normalizeVm(source, branchId)
  }

  async save(vm: ItemRequestVm): Promise<any> {
    const url = endpoints.itemRequest.save
    log('[ItemRequestApi.save] Request', { url, payload: vm })
    const response = await apiClient.post(url, vm)
    log('[ItemRequestApi.save] Response', { url, status: response.status, response: response.data })
    return response.data
  }

  async update(vm: ItemRequestVm): Promise<any> {
    const url = endpoints.itemRequest.update
    log('[ItemRequestApi.update] Request', { url, payload: vm })
    const response = await apiClient.put(url, vm)
    log('[ItemRequestApi.update] Response', { url, status: response.status, response: response.data })
    return response.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.itemRequest.delete(id)
    const response = await apiClient.delete(url)
    log('[ItemRequestApi.delete] Response', { url, status: response.status, response: response.data })
    return response.data
  }

  async getAvailableQuantityForEmployeeDepartment(params: { itemId: number; branchId: number; departmentId: number }): Promise<number> {
    const url = endpoints.itemRequest.getAvailableQuantityForEmployeeDepartment(params)
    const response = await apiClient.get<ApiResponse<any> | any>(url, { skipHandledLocallyErrorUI: true } as any)
    log('[ItemRequestApi.getAvailableQty] Response', { url, status: response.status, response: response.data })
    const raw = unwrap(response.data)
    const direct = Number(raw)
    if (Number.isFinite(direct)) return direct
    const inside = Number((raw && typeof raw === 'object' ? (raw as any).availableQuantity ?? (raw as any).qty ?? (raw as any).quantity : undefined))
    if (Number.isFinite(inside)) return inside
    return 0
  }
}

export const itemRequestApiService = new ItemRequestApiService()

